import { useMemo, type ReactNode } from "react";
import { ChevronsUpDown } from "lucide-react";

import { JsonBlock } from "@/components/common/json-block";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import type { SceneBlock, SceneBrandkit, SocialPostGenerationTaskDetail } from "../types";
import { getKonvaPropsPreview } from "../utils";
import {
  getImageCandidateStatusLabel,
  getSelectedImageCandidateAsset,
} from "./image-candidate-groups";

function InspectorDisclosure({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Collapsible className="rounded-2xl border bg-card p-4">
      <CollapsibleTrigger className="flex w-full items-center justify-between text-left text-sm font-semibold">
        <span>{title}</span>
        <ChevronsUpDown className="size-4 text-muted-foreground" aria-hidden="true" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function isColorString(value: string): boolean {
  const colorPattern = /^(#([0-9a-fA-F]{3,8})|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-zA-Z]+)$/u;
  return colorPattern.test(value.trim());
}

function renderPropertyValue(value: unknown): ReactNode {
  if (typeof value === "string") {
    if (isColorString(value)) {
      return (
        <div className="inline-flex items-center gap-2">
          <span
            className="inline-block size-4 rounded border"
            style={{ backgroundColor: value }}
            aria-label={`color ${value}`}
          />
          <span className="font-mono">{value}</span>
        </div>
      );
    }
    return <span className="break-words">{value}</span>;
  }

  if (typeof value === "number") {
    return <span className="font-mono">{value}</span>;
  }

  if (typeof value === "boolean") {
    return <span>{value ? "true" : "false"}</span>;
  }

  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (Array.isArray(value)) {
    return <span className="font-mono">[{value.join(", ")}]</span>;
  }

  return <span className="font-mono">{JSON.stringify(value)}</span>;
}

export function SceneBlockInspector({
  block,
  linkedAssets,
  imageCandidateGroups,
  brandkit,
  selectedCarouselSlideId,
}: {
  block: SceneBlock | null;
  linkedAssets: SocialPostGenerationTaskDetail["linkedAssets"];
  imageCandidateGroups: SocialPostGenerationTaskDetail["imageCandidateGroups"];
  brandkit: SceneBrandkit;
  selectedCarouselSlideId?: string | null;
}) {
  const imageAssetByBlockId = useMemo(
    () => new Map(linkedAssets.map((asset) => [asset.blockId, asset] as const)),
    [linkedAssets],
  );

  const imageAsset = block?.type === "image" ? imageAssetByBlockId.get(block.id) : null;
  const selectedImageCandidate =
    block?.type === "image"
      ? getSelectedImageCandidateAsset(imageCandidateGroups, block.id, selectedCarouselSlideId)
      : null;
  const logoAsset =
    block?.type === "logo" && block.assetObjectKey === brandkit.logo?.objectKey
      ? brandkit.logo
      : null;

  const blockProperties = useMemo(() => {
    if (!block) return [];

    const entries: Array<{ label: string; value: unknown }> = [
      { label: "ID", value: block.id },
      { label: "Type", value: block.type },
      { label: "X", value: Math.round(block.x) },
      { label: "Y", value: Math.round(block.y) },
      { label: "Width", value: Math.round(block.width) },
      { label: "Height", value: Math.round(block.height) },
      { label: "Rotation", value: `${block.rotation} deg` },
      { label: "Opacity", value: block.opacity },
      { label: "Visible", value: block.visible },
    ];

    if (block.type === "text") {
      entries.push(
        { label: "Text", value: block.text },
        { label: "Font family", value: block.fontFamily },
        { label: "Font size", value: block.fontSize },
        { label: "Font style", value: block.fontStyle },
        { label: "Text color", value: block.fill },
        { label: "Stroke", value: block.stroke ?? null },
        { label: "Stroke width", value: block.strokeWidth ?? null },
      );
    }

    if (block.type === "image") {
      entries.push(
        { label: "Fit", value: block.fit },
        { label: "Corner radius", value: block.cornerRadius },
        { label: "Background mode", value: block.source.backgroundMode },
        { label: "Prompt", value: block.source.prompt },
      );
      if (imageAsset) {
        entries.push(
          { label: "Asset", value: imageAsset.generatedAssetId },
          {
            label: "Asset size",
            value: `${imageAsset.width ?? "?"} x ${imageAsset.height ?? "?"}`,
          },
        );
      }
      if (selectedImageCandidate) {
        const candidateScore = selectedImageCandidate.qaResult?.overallScorePct;
        entries.push(
          {
            label: "Candidate",
            value: `#${selectedImageCandidate.candidateIndex} (${selectedImageCandidate.assetVariant ?? "asset"})`,
          },
          {
            label: "Candidate QA",
            value: `${getImageCandidateStatusLabel(selectedImageCandidate.selectionStatus)} · ${typeof candidateScore === "number" ? `${candidateScore}%` : "--"}`,
          },
        );
      }
    }

    if (block.type === "logo") {
      entries.push(
        { label: "Fit", value: block.fit },
        { label: "Corner radius", value: block.cornerRadius },
        { label: "Object key", value: block.assetObjectKey ?? "(missing in legacy plan)" },
        { label: "Signed URL", value: logoAsset ? "ready" : "missing" },
      );
    }

    if (block.type === "rect") {
      entries.push(
        { label: "Fill", value: block.fill },
        { label: "Stroke", value: block.stroke ?? null },
        { label: "Stroke width", value: block.strokeWidth },
        { label: "Corner radius", value: block.cornerRadius },
      );
    }

    if (block.type === "shape") {
      entries.push(
        { label: "Shape", value: block.shapeType },
        { label: "Fill", value: block.fill ?? null },
        { label: "Stroke", value: block.stroke ?? null },
        { label: "Stroke width", value: block.strokeWidth },
        { label: "Corner radius", value: block.cornerRadius },
        { label: "Line cap", value: block.lineCap ?? null },
        { label: "Line join", value: block.lineJoin ?? null },
        { label: "Dash", value: block.dash ?? null },
        { label: "Points", value: block.points ?? null },
        { label: "Closed", value: block.closed ?? null },
        { label: "Tension", value: block.tension ?? null },
        { label: "Sides", value: block.sides ?? null },
        { label: "Radius", value: block.radius ?? null },
        { label: "Inner radius", value: block.innerRadius ?? null },
        { label: "Outer radius", value: block.outerRadius ?? null },
        { label: "Angle", value: block.angle ?? null },
        { label: "Clockwise", value: block.clockwise ?? null },
        { label: "Path data", value: block.pathData ?? null },
      );
    }

    if (block.type === "frame") {
      entries.push(
        { label: "Fill", value: block.fill ?? null },
        { label: "Stroke", value: block.stroke },
        { label: "Stroke width", value: block.strokeWidth },
        { label: "Corner radius", value: block.cornerRadius },
        { label: "Inset padding", value: block.insetPadding },
      );
    }

    if (block.type === "group") {
      entries.push(
        { label: "Clip", value: block.clip },
        { label: "Padding", value: block.padding },
        { label: "Children", value: block.children.length },
      );
    }

    return entries;
  }, [block, imageAsset, logoAsset, selectedImageCandidate]);

  if (!block) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/10 p-4 text-sm text-muted-foreground">
        Select an element in the template preview to inspect its metadata.
      </div>
    );
  }

  const layoutProperties = blockProperties.filter((property) =>
    ["X", "Y", "Width", "Height", "Rotation"].includes(property.label),
  );
  const detailProperties = blockProperties.filter(
    (property) =>
      !["ID", "Type", "Visible", "Opacity"].includes(property.label) &&
      !layoutProperties.includes(property),
  );

  const renderPropertyRows = (properties: typeof blockProperties) => (
    <div className="grid gap-2">
      {properties.map((property) => (
        <div
          key={property.label}
          className="grid grid-cols-[104px_minmax(0,1fr)] items-start gap-3 text-xs"
        >
          <span className="text-muted-foreground">{property.label}</span>
          <span className="min-w-0">{renderPropertyValue(property.value)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-muted/20 p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Element Summary
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <h4 className="truncate text-lg font-semibold tracking-tight">{block.id}</h4>
          <span className="rounded-full border bg-background px-2 py-0.5 font-mono text-xs text-muted-foreground">
            {block.type}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          <div className="rounded-xl border bg-background px-3 py-2">
            <div className="text-muted-foreground">State</div>
            <div className="mt-1 font-medium text-foreground">
              {block.visible ? "Visible" : "Hidden"}
            </div>
          </div>
          <div className="rounded-xl border bg-background px-3 py-2">
            <div className="text-muted-foreground">Opacity</div>
            <div className="mt-1 font-mono text-foreground">{block.opacity}</div>
          </div>
          <div className="rounded-xl border bg-background px-3 py-2">
            <div className="text-muted-foreground">Size</div>
            <div className="mt-1 font-mono text-foreground">
              {Math.round(block.width)} x {Math.round(block.height)}
            </div>
          </div>
          <div className="rounded-xl border bg-background px-3 py-2">
            <div className="text-muted-foreground">Rotation</div>
            <div className="mt-1 font-mono text-foreground">{block.rotation} deg</div>
          </div>
        </div>
      </div>

      <InspectorDisclosure title="Position & size">
        {renderPropertyRows(layoutProperties)}
      </InspectorDisclosure>

      {detailProperties.length > 0 ? (
        <InspectorDisclosure title="Content & rendering">
          {renderPropertyRows(detailProperties)}
        </InspectorDisclosure>
      ) : null}

      <InspectorDisclosure title="Renderer data">
        <JsonBlock value={getKonvaPropsPreview(block)} />
      </InspectorDisclosure>
    </div>
  );
}
